import * as THREE from "three";
import type { PlanOpening, PlanRoom, Tour, Vec2 } from "@pisorama/shared";
import { modelBounds, wallBox, wallPieces } from "../lib/modelGeometry";
import { EYE_HEIGHT, WALK_WALL_HEIGHT, DOOR_HEIGHT, edgeSpans, roomAt, signedArea } from "../lib/walkGeometry";

/*
 * /de-plano-a-3d · /en/plan-to-3d · /fr/plan-to-3d… — the example flat told as one scroll: the plan lies
 * flat like a drawing, the walls rise into a scale model, then the camera drops into
 * each room and its own 360° photo paints the walls (the Paseo projection), and pulls
 * back out for the CTA. Data comes from the public example tour, so swapping that tour
 * changes this page too. Scroll position is the only clock: every frame is a pure
 * function of it (smoothed), so scrolling back plays the story backwards.
 *
 * Steps are the `.step` sections inside `section.p3` (`data-step="plan|rise|room:<nodeId>|final"`);
 * a room step whose node is missing from the tour is removed. It runs only on its own pages;
 * the home page links to them with a teaser card.
 */

const TOUR_URL = "/tours/ejemplo/tour.json";
const TOUR_BASE = "/tours/ejemplo/";

const COLOR = {
  paper: "#FFFFFF",
  grid: "#ECE8E2",
  ink: "#1A1714",
  /** Walls lying flat read as poché, the filled cut of a plan drawing. */
  poche: "#3A342F",
  wall: "#FFFFFF",
  floor: "#F7F6F3",
  glass: "#DCE6EA",
  accent: "#B08D57",
} as const;

const FOV_OUT = 30;
const FOV_IN = 72;
/** Share of each step spent holding still (the rest is the camera move). */
const HOLD = 0.45;
/** Inside a room the view pans this far (rad) either side while you read. */
const PAN = 0.55;
/** Eye-level pitch in a room (rad): a touch down, so floor and ceiling both show. */
const ROOM_PITCH = -0.05;
/** Room to room: rise straight up over the walls, pitch down to see the plan, drop in. */
const HOP_MIN_HEIGHT = 7;
const HOP_HEIGHT_PER_M = 1.3;
const HOP_PITCH = -1.25;
const HOP_FOV = 58;
/** Final step idle orbit (rad/s), off with prefers-reduced-motion. */
const ORBIT_SPEED = 0.05;
/** A room's photo fades in over this long (ms) once it has loaded. */
const PHOTO_FADE_MS = 450;
const TEXTURE_WIDTH = 2048;
const INSET = 0.065;
/** The example flat's Spanish room names, per page language (es shows them as they are). */
const ROOM_NAMES: Record<string, Record<string, string>> = {
  en: { Dormitorio: "Bedroom", "Baño": "Bathroom", Recibidor: "Hall", Comedor: "Living-dining room", Salón: "Living room", Cocina: "Kitchen" },
  fr: { Dormitorio: "Chambre", "Baño": "Salle de bains", Recibidor: "Entrée", Comedor: "Séjour", Salón: "Salon", Cocina: "Cuisine" },
  de: { Dormitorio: "Schlafzimmer", "Baño": "Bad", Recibidor: "Diele", Comedor: "Wohn-Esszimmer", Salón: "Wohnzimmer", Cocina: "Küche" },
  it: { Dormitorio: "Camera da letto", "Baño": "Bagno", Recibidor: "Ingresso", Comedor: "Soggiorno-pranzo", Salón: "Soggiorno", Cocina: "Cucina" },
  pt: { Dormitorio: "Quarto", "Baño": "Banheiro", Recibidor: "Hall de entrada", Comedor: "Sala de jantar", Salón: "Sala de estar", Cocina: "Cozinha" },
};

const vertexShader = /* glsl */ `
varying vec3 vWorld;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vWorld = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

// Same projection as the Paseo (lib/walk.ts ↔ walkGeometry.panoUv), plus a fade-in.
const fragmentShader = /* glsl */ `
uniform sampler2D map;
uniform vec3 center;
uniform float panoYaw;
uniform float ready;
uniform vec3 base;
varying vec3 vWorld;
const float PI = 3.141592653589793;
void main() {
  vec3 d = vWorld - center;
  float c = cos(-panoYaw);
  float s = sin(-panoYaw);
  float cx = c * d.x - s * d.z;
  float cz = s * d.x + c * d.z;
  float yaw = atan(cz, cx);
  float pitch = atan(d.y, length(vec2(cx, cz)));
  vec2 uv = vec2((yaw + PI) / (2.0 * PI), 0.5 + pitch / PI);
  vec3 photo = texture2D(map, uv).rgb;
  gl_FragColor = vec4(mix(base, photo, ready), 1.0);
  #include <colorspace_fragment>
}`;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (a: number, b: number, v: number) => {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

interface Pose {
  pos: THREE.Vector3;
  target: THREE.Vector3;
  fov: number;
  /** 1 = looking at the whole model (labels, offset clear of the cards), 0 = in a room. */
  overview: number;
  /** Plan heading of the view (rad), set on room poses only. */
  heading?: number;
}

const lookDir = (heading: number, pitch: number) =>
  new THREE.Vector3(Math.cos(pitch) * Math.cos(heading), Math.sin(pitch), Math.cos(pitch) * Math.sin(heading));

/** a → b the short way round. */
const lerpAngle = (a: number, b: number, t: number) => {
  const d = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + d * t;
};

async function loadPanoTexture(url: string): Promise<THREE.Texture> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`pano ${res.status}`);
  const bitmap = await createImageBitmap(await res.blob());
  const canvas = document.createElement("canvas");
  canvas.width = TEXTURE_WIDTH;
  canvas.height = TEXTURE_WIDTH / 2;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = false; // the atan seam would pick the smallest mip
  texture.minFilter = THREE.LinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  return texture;
}

function centroid(outline: Vec2[]): Vec2 {
  const n = outline.length;
  return { x: outline.reduce((s, p) => s + p.x, 0) / n, y: outline.reduce((s, p) => s + p.y, 0) / n };
}

function line(points: THREE.Vector3[], material: THREE.LineBasicMaterial): THREE.Line {
  return new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), material);
}

async function boot(root: HTMLElement): Promise<void> {
  const lang = document.documentElement.lang.slice(0, 2);
  // English writes 12.5 m²; every other language on the site uses a decimal comma.
  const en = lang === "en";
  const stage = root.querySelector<HTMLElement>(".p3-stage")!;
  const labelLayer = root.querySelector<HTMLElement>(".p3-labels")!;
  const counter = root.querySelector<HTMLElement>(".p3-counter");
  const loading = root.querySelector<HTMLElement>(".p3-loading");
  const tour = (await (await fetch(TOUR_URL)).json()) as Tour;
  const plan = tour.floorPlan;
  if (!plan?.walls?.length || !plan.rooms?.length) throw new Error("example tour has no plan");
  const rooms: PlanRoom[] = plan.rooms;
  const openings: PlanOpening[] = plan.openings ?? [];
  const panoYaw = plan.panoYaw ?? 0;
  const roomName = (name: string) => ROOM_NAMES[lang]?.[name] ?? name;

  // Steps from the page; drop room steps the tour can't fill.
  const steps = [...root.querySelectorAll<HTMLElement>(".step")].filter((el) => {
    const id = el.dataset.step?.startsWith("room:") ? el.dataset.step.slice(5) : null;
    const ok = !id || tour.nodes.some((n) => n.id === id && roomAt(n.plan, rooms) >= 0);
    if (!ok) el.remove();
    return ok;
  });

  // ---- renderer ----------------------------------------------------------------
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  stage.append(renderer.domElement);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(COLOR.paper);
  const camera = new THREE.PerspectiveCamera(FOV_OUT, 1, 0.05, 200);
  scene.add(new THREE.HemisphereLight("#ffffff", "#cfc6ba", 2.2));
  const sun = new THREE.DirectionalLight("#ffffff", 1.2);
  sun.position.set(-6, 12, -4);
  scene.add(sun);

  const bounds = modelBounds(plan.walls, tour.nodes.map((n) => n.plan));
  const center = new THREE.Vector3(bounds.center[0], 0, bounds.center[2]);
  const xs = plan.walls.flatMap((w) => [w.a.x, w.b.x]);
  const zs = plan.walls.flatMap((w) => [w.a.y, w.b.y]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minZ = Math.min(...zs), maxZ = Math.max(...zs);
  const width = maxX - minX, depth = maxZ - minZ;

  // Drawing sheet grid (0.5 m), fades as the walls rise.
  const grid = new THREE.GridHelper(Math.ceil(bounds.span * 3), Math.ceil(bounds.span * 6), COLOR.grid, COLOR.grid);
  grid.position.copy(center).setY(-0.002);
  const gridMaterial = grid.material as THREE.LineBasicMaterial;
  gridMaterial.transparent = true;
  scene.add(grid);

  // ---- walls: one group scaled in y, so they grow out of the drawing -----------------
  const walls = new THREE.Group();
  scene.add(walls);
  const wallMaterial = new THREE.MeshLambertMaterial({ color: COLOR.poche });
  const glassMaterial = new THREE.MeshLambertMaterial({ color: COLOR.glass, transparent: true, opacity: 0.55 });
  const edgeMaterial = new THREE.LineBasicMaterial({ color: COLOR.ink, transparent: true, opacity: 0.55 });
  const addBox = (piece: { a: Vec2; b: Vec2 }, y0: number, y1: number, material: THREE.Material) => {
    const box = wallBox({ a: piece.a, b: piece.b, height: y1 - y0 });
    if (!box) return;
    const geometry = new THREE.BoxGeometry(...box.size);
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(box.center[0], y0 + (y1 - y0) / 2, box.center[2]);
    mesh.rotation.y = box.rotationY;
    mesh.add(new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgeMaterial));
    walls.add(mesh);
  };
  for (const piece of wallPieces(plan.walls, openings)) {
    if (piece.kind === "solid") addBox(piece.wall, 0, WALK_WALL_HEIGHT, wallMaterial);
    else {
      addBox(piece.wall, 0, 0.9, wallMaterial);
      addBox(piece.wall, 0.9, 2.1, glassMaterial);
      addBox(piece.wall, 2.1, WALK_WALL_HEIGHT, wallMaterial);
    }
  }
  for (const o of openings.filter((o) => o.kind === "door")) addBox(o, DOOR_HEIGHT, WALK_WALL_HEIGHT, wallMaterial);

  // ---- plan annotations: door swings, overall dimensions (fade as walls rise) -------
  const annotation = new THREE.LineBasicMaterial({ color: COLOR.ink, transparent: true });
  const annotations = new THREE.Group();
  scene.add(annotations);
  for (const o of openings.filter((o) => o.kind === "door")) {
    const r = Math.hypot(o.b.x - o.a.x, o.b.y - o.a.y);
    const start = Math.atan2(o.b.y - o.a.y, o.b.x - o.a.x);
    const pts: THREE.Vector3[] = [];
    for (let k = 0; k <= 16; k++) {
      const t = start + (k / 16) * (Math.PI / 2);
      pts.push(new THREE.Vector3(o.a.x + Math.cos(t) * r, 0.01, o.a.y + Math.sin(t) * r));
    }
    annotations.add(line(pts, annotation));
    annotations.add(line([new THREE.Vector3(o.a.x, 0.01, o.a.y), pts[pts.length - 1]!], annotation));
  }
  // Windows: the drawn double line across the wall.
  const windowLine = new THREE.LineBasicMaterial({ color: COLOR.paper, transparent: true });
  for (const o of openings.filter((o) => o.kind === "window")) {
    const len = Math.hypot(o.b.x - o.a.x, o.b.y - o.a.y);
    const nx = -(o.b.y - o.a.y) / len * 0.045, nz = (o.b.x - o.a.x) / len * 0.045;
    for (const k of [-1, 1]) {
      annotations.add(line([new THREE.Vector3(o.a.x + nx * k, 0.06, o.a.y + nz * k), new THREE.Vector3(o.b.x + nx * k, 0.06, o.b.y + nz * k)],
        windowLine));
    }
  }
  const DIM = 0.7;
  const dims: Array<{ a: THREE.Vector3; b: THREE.Vector3; text: string }> = [
    { a: new THREE.Vector3(minX, 0, minZ - DIM), b: new THREE.Vector3(maxX, 0, minZ - DIM), text: width.toFixed(2) },
    { a: new THREE.Vector3(minX - DIM, 0, minZ), b: new THREE.Vector3(minX - DIM, 0, maxZ), text: depth.toFixed(2) },
  ];
  for (const d of dims) {
    annotations.add(line([d.a, d.b], annotation));
    const n = d.a.x === d.b.x ? new THREE.Vector3(0.18, 0, 0) : new THREE.Vector3(0, 0, 0.18);
    for (const p of [d.a, d.b]) annotations.add(line([p.clone().sub(n), p.clone().add(n)], annotation));
  }

  // ---- rooms: floors, and inner skins painted with the room's photo ---------------
  interface RoomView { node?: Tour["nodes"][number]; material: THREE.Material; skins: THREE.Object3D[]; paint: number; loadedAt?: number; failed?: boolean }
  const roomViews: RoomView[] = rooms.map((room, i) => {
    const node = tour.nodes.find((n) => roomAt(n.plan, rooms) === i);
    const material = node
      ? new THREE.ShaderMaterial({
          vertexShader,
          fragmentShader,
          uniforms: {
            map: { value: null },
            center: { value: new THREE.Vector3(node.plan.x, EYE_HEIGHT, node.plan.y) },
            panoYaw: { value: panoYaw },
            ready: { value: 0 },
            base: { value: new THREE.Color(COLOR.floor) },
          },
        })
      : new THREE.MeshLambertMaterial({ color: COLOR.floor });
    const shape = new THREE.Shape(room.outline.map((p) => new THREE.Vector2(p.x, -p.y)));
    const floor = new THREE.Mesh(new THREE.ShapeGeometry(shape), material);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0.003;
    scene.add(floor);
    const skins: THREE.Object3D[] = [];
    if (node) {
      // Ceiling faces down: culled from above, seen from inside.
      const ceilingGeometry = new THREE.ShapeGeometry(shape);
      const index = ceilingGeometry.index!;
      for (let t = 0; t < index.count; t += 3) {
        const second = index.getX(t + 1);
        index.setX(t + 1, index.getX(t + 2));
        index.setX(t + 2, second);
      }
      const ceiling = new THREE.Mesh(ceilingGeometry, material);
      ceiling.rotation.x = -Math.PI / 2;
      ceiling.position.y = WALK_WALL_HEIGHT - 0.01;
      // Inward quads just inside the wall boxes; shortened at the ends so corners meet.
      const ccw = signedArea(room.outline) > 0;
      const positions: number[] = [];
      room.outline.forEach((a, k) => {
        const b = room.outline[(k + 1) % room.outline.length]!;
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 0.01) return;
        const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
        // Interior is to the left of a→b for a counter-clockwise outline (plan x/y).
        const nx = ccw ? -uy : uy, ny = ccw ? ux : -ux;
        for (const span of edgeSpans(a, b, openings)) {
          const ta = Math.hypot(span.a.x - a.x, span.a.y - a.y), tb = Math.hypot(span.b.x - a.x, span.b.y - a.y);
          const s0 = Math.max(ta, INSET), s1 = Math.min(tb, len - INSET);
          if (s1 - s0 < 0.01) continue;
          const p = { x: a.x + ux * s0 + nx * INSET, y: a.y + uy * s0 + ny * INSET };
          const q = { x: a.x + ux * s1 + nx * INSET, y: a.y + uy * s1 + ny * INSET };
          const y0 = span.door ? DOOR_HEIGHT : 0;
          const [m, o] = ccw ? [p, q] : [q, p];
          positions.push(m.x, y0, m.y, o.x, y0, o.y, o.x, WALK_WALL_HEIGHT, o.y, m.x, y0, m.y, o.x, WALK_WALL_HEIGHT, o.y, m.x, WALK_WALL_HEIGHT, m.y);
        }
      });
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      const wallSkin = new THREE.Mesh(geometry, material);
      skins.push(ceiling, wallSkin);
      skins.forEach((s) => { s.visible = false; scene.add(s); });
    }
    return { node, material, skins, paint: 0 };
  });
  roomViews.forEach((view) => {
    if (!view.node) return;
    loadPanoTexture(TOUR_BASE + (view.node.panoramaLow ?? view.node.panorama))
      .then((texture) => {
        (view.material as THREE.ShaderMaterial).uniforms.map!.value = texture;
        view.loadedAt = performance.now();
        dirty = true;
      })
      .catch(() => { view.failed = true; dirty = true; /* room stays plain */ });
  });

  // ---- HTML labels projected from the plan ------------------------------------------
  const labels = rooms.map((room) => {
    const el = document.createElement("div");
    el.className = "room-label";
    const area = Math.abs(signedArea(room.outline));
    el.innerHTML = `<strong></strong><span></span>`;
    el.querySelector("strong")!.textContent = roomName(room.name);
    el.querySelector("span")!.textContent = `${area.toFixed(1).replace(".", en ? "." : ",")} m²`;
    labelLayer.append(el);
    const c = centroid(room.outline);
    return { el, at: new THREE.Vector3(c.x, 0, c.y) };
  });
  const dimLabels = dims.map((d) => {
    const el = document.createElement("div");
    el.className = "dim-label";
    el.textContent = `${en ? d.text : d.text.replace(".", ",")} m`;
    labelLayer.append(el);
    return { el, at: d.a.clone().add(d.b).multiplyScalar(0.5) };
  });

  // ---- camera poses, one per step ----------------------------------------------------
  const fitDistance = (w: number, d: number, fov: number) => {
    const t = Math.tan(THREE.MathUtils.degToRad(fov) / 2);
    // Usable share of the frame next to (wide) or above (phone) the caption cards.
    const wide = stage.clientWidth >= 900;
    return Math.max(d / 2 / (t * (wide ? 0.82 : 0.5)), w / 2 / (t * camera.aspect * (wide ? 0.64 : 0.92)));
  };
  // Idle orbit on the final step, eased in by scroll so it never jumps.
  let orbit = 0;
  let orbitNow = 0;
  const posesFor = (kind: string): ((u: number) => Pose) => {
    if (kind === "plan") {
      return () => {
        const h = fitDistance(width + 2 * DIM + 1.2, depth + 2 * DIM + 1.8, FOV_OUT);
        return { pos: center.clone().add(new THREE.Vector3(0, h, h * 0.002)), target: center.clone(), fov: FOV_OUT, overview: 1 };
      };
    }
    if (kind === "rise" || kind === "final") {
      return (u) => {
        const az = Math.PI / 4 + (kind === "final" ? u * 0.8 + orbitNow : u * 0.3);
        const polar = THREE.MathUtils.degToRad(kind === "final" ? 52 : 56);
        const dist = fitDistance(bounds.span * 1.25, bounds.span * 1.05, FOV_OUT);
        const dir = new THREE.Vector3(Math.sin(polar) * Math.cos(az), Math.cos(polar), Math.sin(polar) * Math.sin(az));
        return { pos: center.clone().add(dir.multiplyScalar(dist)), target: center.clone().setY(0.6), fov: FOV_OUT, overview: 1 };
      };
    }
    const node = tour.nodes.find((n) => n.id === kind.slice(5))!;
    // Start on the view the tour opens with (a pano yaw; on the plan it turns by panoYaw).
    const heading0 = node.defaultYaw + panoYaw;
    return (u) => {
      const h = heading0 + (u / (HOLD / 2)) * PAN;
      const pos = new THREE.Vector3(node.plan.x, EYE_HEIGHT, node.plan.y);
      return { pos, target: pos.clone().add(lookDir(h, ROOM_PITCH)), fov: FOV_IN, overview: 0, heading: h };
    };
  };
  const stepKinds = steps.map((el) => el.dataset.step ?? "plan");
  let poses = stepKinds.map(posesFor);
  const roomIndexOfStep = stepKinds.map((k) => {
    if (!k.startsWith("room:")) return -1;
    const node = tour.nodes.find((n) => n.id === k.slice(5))!;
    return roomAt(node.plan, rooms);
  });
  const riseStep = Math.max(0, stepKinds.indexOf("rise"));
  const finalStep = stepKinds.indexOf("final");

  /**
   * Room to room without the busy mid-frame: rise straight up out of the room, turn to
   * the direction of travel while pitching down over the model, glide across, drop into
   * the next room and turn to its opening view. Yaw and pitch only, so the horizon never
   * rolls; labels stay hidden (overview 0) the whole way.
   */
  function hop(a: Pose, b: Pose, m: number): Pose {
    const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z;
    const travel = Math.hypot(dx, dz) > 0.01 ? Math.atan2(dz, dx) : a.heading ?? 0;
    const lift = Math.sin(Math.PI * m);
    const top = Math.max(HOP_MIN_HEIGHT, Math.hypot(dx, dz) * HOP_HEIGHT_PER_M);
    const pos = a.pos.clone().lerp(b.pos, smooth(0.2, 0.8, m));
    pos.y = EYE_HEIGHT + (top - EYE_HEIGHT) * lift;
    // Keep facing the way of travel until the drop, or the high view swings off the model.
    const heading = m < 0.5 ? lerpAngle(a.heading ?? travel, travel, smooth(0, 0.5, m)) : lerpAngle(travel, b.heading ?? travel, smooth(0.65, 1, m));
    const pitch = THREE.MathUtils.lerp(ROOM_PITCH, HOP_PITCH, lift);
    const fov = THREE.MathUtils.lerp(FOV_IN, HOP_FOV, lift);
    return { pos, target: pos.clone().add(lookDir(heading, pitch)), fov, overview: 0 };
  }

  function poseAt(s: number): Pose {
    const last = steps.length - 1;
    const k = Math.min(last, Math.max(0, Math.floor(s)));
    const t = s - k;
    if (k === last || t <= HOLD / 2) return poses[k]!(Math.min(t, HOLD / 2));
    if (t >= 1 - HOLD / 2) return poses[k + 1]!(t - 1);
    const a = poses[k]!(HOLD / 2);
    const b = poses[k + 1]!(-HOLD / 2);
    const m = ease((t - HOLD / 2) / (1 - HOLD));
    const inA = a.overview === 0, inB = b.overview === 0;
    if (inA && inB) return hop(a, b, m);
    const pos = a.pos.clone().lerp(b.pos, m);
    // Leaving or entering a room: lift over the walls instead of going through them.
    if (inA || inB) pos.y += Math.sin(Math.PI * m) * 3;
    const fovM = inA && !inB ? smooth(0, 0.4, m) : !inA && inB ? smooth(0.6, 1, m) : m;
    const fov = THREE.MathUtils.lerp(a.fov, b.fov, fovM);
    const overview = inA ? fovM : inB ? 1 - fovM : 1;
    return { pos, target: a.target.clone().lerp(b.target, m), fov, overview };
  }

  // ---- scroll → story position (smoothed) -------------------------------------------
  let anchors: number[] = [];
  const measure = () => {
    const vh = window.innerHeight;
    const top = (el: HTMLElement) => el.getBoundingClientRect().top + window.scrollY;
    anchors = steps.map((el) => top(el) + el.offsetHeight / 2 - vh / 2);
    // The story starts as soon as the section reaches the top of the screen.
    anchors[0] = Math.max(0, Math.min(anchors[0]!, top(root)));
  };
  const storyAt = (y: number) => {
    if (y <= anchors[0]!) return 0;
    for (let i = 0; i < anchors.length - 1; i++) {
      if (y < anchors[i + 1]!) return i + (y - anchors[i]!) / (anchors[i + 1]! - anchors[i]!);
    }
    return anchors.length - 1;
  };
  const resize = () => {
    const w = stage.clientWidth, h = stage.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    poses = stepKinds.map(posesFor);
    measure();
    dirty = true;
  };
  let dirty = true;
  let target = 0;
  let current = -1;
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  // Off screen (the rest of the home page), nothing renders.
  let onScreen = true;
  new IntersectionObserver(([entry]) => {
    onScreen = entry?.isIntersecting ?? true;
    if (onScreen) { measure(); dirty = true; }
  }).observe(root);
  window.addEventListener("resize", resize);
  window.addEventListener("scroll", () => { target = storyAt(window.scrollY); }, { passive: true });
  resize();
  target = storyAt(window.scrollY);

  const project = new THREE.Vector3();
  const place = (el: HTMLElement, at: THREE.Vector3, opacity: number) => {
    project.copy(at).project(camera);
    const behind = project.z > 1;
    el.style.opacity = behind ? "0" : opacity.toFixed(3);
    el.style.transform = `translate(-50%, -50%) translate(${((project.x + 1) / 2) * stage.clientWidth}px, ${((1 - project.y) / 2) * stage.clientHeight}px)`;
  };

  function frame(s: number) {
    const rise = smooth(0.1, riseStep || 1, s);
    walls.scale.y = Math.max(0.02, rise);
    wallMaterial.color.set(COLOR.poche).lerp(new THREE.Color(COLOR.wall), smooth(0, 0.35, rise));
    edgeMaterial.opacity = 0.15 + 0.45 * smooth(0, 0.3, rise);
    gridMaterial.opacity = 1 - smooth(0.2, 0.7, rise);
    annotation.opacity = 1 - smooth(0, 0.5, rise);
    windowLine.opacity = annotation.opacity;
    annotations.visible = annotation.opacity > 0.01;

    // A room's photo fades in on the way to its step and stays; one that arrives late
    // fades in on its own rather than popping.
    const now = performance.now();
    roomViews.forEach((r, i) => {
      const step = roomIndexOfStep.indexOf(i);
      r.paint = step < 0 ? 0 : smooth(step - 0.8, step - 0.45, s);
      if (r.material instanceof THREE.ShaderMaterial) {
        const arrived = r.loadedAt === undefined ? 0 : smooth(0, PHOTO_FADE_MS, now - r.loadedAt);
        if (arrived > 0 && arrived < 1) dirty = true;
        r.material.uniforms.ready!.value = r.paint * arrived;
      }
      r.skins.forEach((m) => { m.visible = r.paint > 0.001; });
    });

    orbitNow = finalStep < 0 ? 0 : orbit * smooth(finalStep - 0.5, finalStep - 0.1, s);
    const pose = poseAt(s);
    camera.position.copy(pose.pos);
    camera.fov = pose.fov;
    camera.lookAt(pose.target);
    const inside = 1 - pose.overview;
    // Keep the model clear of the caption cards: right of them on wide screens, above
    // them on phones. Inside a room the photo fills the frame, so no offset.
    const w = stage.clientWidth, h = stage.clientHeight;
    const wide = w >= 900;
    camera.setViewOffset(w, h, wide ? -w * 0.17 * (1 - inside) : 0, wide ? 0 : h * 0.09 * (1 - inside), w, h);
    camera.updateProjectionMatrix();

    labels.forEach((l) => place(l.el, l.at, 1 - inside));
    dimLabels.forEach((l) => place(l.el, l.at, 1 - smooth(0, 0.5, rise)));

    const active = Math.round(s);
    const activeRoom = roomViews[roomIndexOfStep[active] ?? -1];
    loading?.classList.toggle("is-visible", !!activeRoom && activeRoom.loadedAt === undefined && !activeRoom.failed);
    steps.forEach((el, i) => el.classList.toggle("is-active", i === active));
    if (counter) counter.textContent = `${String(active + 1).padStart(2, "0")} / ${String(steps.length).padStart(2, "0")}`;
    renderer.render(scene, camera);
  }

  let lastTime = performance.now();
  const tick = (time: number) => {
    const dt = Math.min(0.1, Math.max(0, (time - lastTime) / 1000));
    lastTime = time;
    if (!onScreen) { requestAnimationFrame(tick); return; }
    if (finalStep >= 0 && !reduceMotion) {
      if (current > finalStep - 0.3 && Math.abs(target - current) < 0.02) {
        orbit += dt * ORBIT_SPEED;
        dirty = true;
      } else if (current < finalStep - 0.6) orbit = 0; // weight is 0 there: no jump
    }
    const next = reduceMotion || current < 0 ? target : current + (target - current) * 0.09;
    if (dirty || Math.abs(next - current) > 1e-4) {
      current = Math.abs(target - next) < 1e-4 ? target : next;
      dirty = false;
      frame(current);
    }
    requestAnimationFrame(tick);
  };
  root.classList.add("showcase-ready");
  requestAnimationFrame(tick);
}

const root = document.querySelector<HTMLElement>("section.p3");
if (root) {
  boot(root).catch((error) => {
    console.error(error);
    root.classList.add("showcase-failed");
  });
}
